import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import UserTable from '../components/UserTable';
import UserForm from '../components/UserForm';

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user: currentUser } = useContext(AuthContext);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/api/admin/users');
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenAddModal = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingUser) {
        // We are editing an existing user
        await apiClient.put(`/api/admin/users/${editingUser.id}`, formData);
      } else {
        // We are creating a new user
        await apiClient.post('/api/admin/users', formData);
      }
      handleCloseModal();
      fetchUsers(); // Refresh the user list
    } catch (err) {
      console.error('Failed to save user:', err);
      // You could set a form-specific error state here
      alert(err.response?.data?.msg || 'Failed to save user.');
    }
  };
  
  if (isLoading) return <div className="card"><p>Loading users...</p></div>;
  if (error) return <div className="card"><p className="text-red-500">{error}</p></div>;

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-slate-800 mb-0">User Management</h1>
        <button onClick={handleOpenAddModal} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
          + Add New User
        </button>
      </div>

      <UserTable users={users} onEdit={handleOpenEditModal} currentUser={currentUser} />

      {isModalOpen && (
        <UserForm
          initialData={editingUser}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseModal}
        />
      )}
    </div>
  );
}

export default UserManagementPage;